import { Formik } from 'formik';
import { useCallback, useContext } from 'react';

import { customersApi } from 'src/app/services/Customers/Customers.service';
import type { ICustomerLeadProductInitialRequestTabFormValues } from 'src/components/customers/CustomerLeadTypes';
import {
  customerLeadInitialRequestTabValidationSchema,
  toCustomerLeadInitialRequestTabInitialValues,
} from 'src/components/customers/steps/Quantities/CustomerLeadStepInitialRequestTab.validation';
import { CustomerLeadStepInitialRequestTabContent } from 'src/components/customers/steps/Quantities/CustomerLeadStepInitialRequestTabContent';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import type { CustomerLeadUrgency } from 'src/types/customers/CustomerLeadUrgency';

type CustomerLeadStepInitialRequestTabProps = {
  customerProductLeadId: string;

  onSubmit: () => boolean;
};

export function CustomerLeadStepInitialRequestTab({
  customerProductLeadId,
  onSubmit,
}: Readonly<CustomerLeadStepInitialRequestTabProps>) {
  const { customerLeadProduct, updateCustomerLeadProduct } = useContext(CustomersContext);

  const [, partialUpdateCustomerLeadProduct] =
    customersApi.usePartialUpdateCustomerProductLeadMutation(customerProductLeadId);

  const save = useCallback(
    async (newValues: ICustomerLeadProductInitialRequestTabFormValues, submit: boolean) => {
      const updatedCustomerProductLeadData = await partialUpdateCustomerLeadProduct({
        data: {
          initialRequest: {
            urgency: newValues.urgency as CustomerLeadUrgency, // Validation doesn't allow null here
            maxQtySingleDelivery: newValues.maxQtySingleDelivery,
            purchaseReasons: newValues.purchaseReasons,

            submit,
          },
        },
      });

      updateCustomerLeadProduct(updatedCustomerProductLeadData.data);
    },
    [partialUpdateCustomerLeadProduct, updateCustomerLeadProduct]
  );

  const handleSubmit = useCallback(
    async (submitValues: ICustomerLeadProductInitialRequestTabFormValues) => {
      await save(submitValues, true);

      return onSubmit();
    },
    [save, onSubmit]
  );

  const handleChange = useCallback(
    (newValues: ICustomerLeadProductInitialRequestTabFormValues) => save(newValues, false),
    [save]
  );

  if (!customerLeadProduct) {
    return null;
  }

  return (
    <Formik<ICustomerLeadProductInitialRequestTabFormValues>
      initialValues={toCustomerLeadInitialRequestTabInitialValues(customerLeadProduct)}
      validationSchema={customerLeadInitialRequestTabValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => <CustomerLeadStepInitialRequestTabContent formikProps={formikProps} onChange={handleChange} />}
    </Formik>
  );
}
